import { defineStore } from "pinia";
import Storage from "good-storage";
import {
  getUserRole,
  setUserRole as saveUserRole,
  getIsLogin,
  setIsLogin as saveIsLogin,
  clearUserAuth,
} from "@/store/session";

export const useSharedStore = defineStore("shared", {
  state: () => ({
    showLoginDialog: false,
    chatListStyle: Storage.get("chat_list_style", "chat"),
    chatStream: Storage.get("chat_stream", true),
    socket: { conn: null, handlers: {} },
    theme: Storage.get("theme", "light"),
    isLogin: getIsLogin(), // 从持久化存储读取
    userRole: getUserRole(), // 从持久化存储读取
    chatListExtend: Storage.get("chat_list_extend", true),

    // Current room context (mainly for student/educator lab pages)
    // - curRoomId: the minimal info required for API calls (room_id injection)
    // - curRoom: optional full room object for UI display
    curRoomId: Storage.get("cur_room_id", ""),
    curRoom: Storage.get("cur_room", {}),

    // 当前激活的智能体角色ID（用于工具页和Chat页之间同步）
    activeRoleId: null,
    activeToolItem: Storage.get("active_tool_item",{type:0}),
  }),
  getters: {},
  actions: {
    setShowLoginDialog(value) {
      this.showLoginDialog = value;
    },
    setChatListStyle(value) {
      this.chatListStyle = value;
      Storage.set("chat_list_style", value);
    },
    setChatStream(value) {
      this.chatStream = value;
      Storage.set("chat_stream", value);
    },
    setSocket(value) {
      this.rebindMessageHandlers(value);
      this.socket.conn = value;
    },
    setChatListExtend(value) {
      this.chatListExtend = value;
      Storage.set("chat_list_extend", value);
    },

    setCurRoomId(roomId) {
      this.curRoomId = roomId ? String(roomId) : "";
      Storage.set("cur_room_id", this.curRoomId);
    },
    setCurRoom(room) {
      this.curRoom = room || {};
      Storage.set("cur_room", this.curRoom);
      // keep a stable room id for API calls
      if (room && room.id) {
        this.setCurRoomId(room.id);
      }
    },
    clearCurRoom() {
      this.curRoom = {};
      this.curRoomId = "";
      Storage.remove("cur_room");
      Storage.remove("cur_room_id");
    },
    // 设置当前激活的智能体角色ID
    setActiveRoleId(roleId) {
      this.activeRoleId = roleId ? parseInt(roleId) : null;
    },
    addMessageHandler(key, callback) {
      // If already registered, remove the old one first to prevent duplicates
      if (this.socket.handlers[key]) {
        this.removeMessageHandler(key);
      }
      // Wrap the callback in a named function so removeEventListener can match it
      const wrappedHandler = (event) => {
        try {
          if (event.data instanceof Blob) {
            const reader = new FileReader();
            reader.readAsText(event.data, "UTF-8");
            reader.onload = () => {
              callback(JSON.parse(String(reader.result)));
            };
          }
        } catch (e) {
          console.warn(e);
        }
      };
      // Store both: wrappedHandler for addEventListener/removeEventListener,
      // original callback reference for setSocket re-binding
      this.socket.handlers[key] = { wrappedHandler, callback };
      if (this.socket.conn) {
        this.socket.conn.addEventListener("message", wrappedHandler);
      }
    },

    // Re-bind all existing handlers to a new WebSocket connection
    // (called from setSocket)
    rebindMessageHandlers(conn) {
      if (!conn) return;
      for (const key in this.socket.handlers) {
        const { wrappedHandler } = this.socket.handlers[key];
        conn.addEventListener("message", wrappedHandler);
      }
    },

    removeMessageHandler(key) {
      const entry = this.socket.handlers[key];
      if (!entry) return;
      if (this.socket.conn && this.socket.conn.readyState === WebSocket.OPEN) {
        this.socket.conn.removeEventListener("message", entry.wrappedHandler);
      }
      delete this.socket.handlers[key];
    },
    setTheme(theme) {
      this.theme = theme;
      document.documentElement.setAttribute("data-theme", theme); // 设置 HTML 的 data-theme 属性
      Storage.set("theme", theme);
    },
    setActiveToolItem(item) {
      this.activeToolItem = item;
      Storage.set("active_tool_item", item);
    },
    setIsLogin(value) {
      this.isLogin = value;
      saveIsLogin(value); // 持久化存储
    },
    setUserRole(value) {
      let role = null;
      switch (value) {
        case 0:
          role = "student";
          break;
        case 1:
          role = "educator";
          break;
        case 2:
          role = "normal";
          break;
        default:
          role = null;
      }
      this.userRole = role;
      saveUserRole(role); // 持久化存储
      return this.userRole;
    },
    // 清除认证状态
    clearAuth() {
      this.isLogin = false;
      this.userRole = null;
      clearUserAuth();
    },
  },
});
